/**
 * Archive URL — parsing, building en AJAX-navigatie.
 *
 * Schone URLs via `buildUrl` (client-side equivalent van
 * `Archives::get_clean_url()`). Form-submit en sort-change gaan via
 * `fetch` naar het REST endpoint `/wp-json/agora/v1/archive`, updaten
 * de DOM-containers (`data-agora-archive-posts`,
 * `data-agora-archive-pagination`) en registreren `pushState` met de
 * schone URL. `popstate` herstelt state bij browser back/forward
 * zonder reload.
 *
 * Error fallback: bij AJAX-failure navigeert de module naar de schone
 * URL (page reload) zodat de gebruiker hoe dan ook resultaten krijgt.
 *
 * @module modules/archive-url
 */

import { createFocusTrap } from './ui/focus-trap.js';
import { initCustomSelects } from './ui/custom-select.js';
import { fadeIn, fadeOut, motionAnimate } from './ui/motion-utils.js';
import { lockScroll, unlockScroll } from './ui/scroll-lock.js';

const SELECTORS = {
    form: '[data-agora-archive]',
    sortSelect: '[data-agora-archive-sort-select]',
    postsContainer: '[data-agora-archive-posts]',
    paginationContainer: '[data-agora-archive-pagination]',
    filtersContainer: '[data-agora-archive-filters]',
    loadMoreButton: '[data-agora-archive-load-more]',
    infiniteScrollContainer: '[data-agora-archive-infinite-scroll]',
    sentinel: '[data-agora-archive-sentinel]',
    infiniteLoading: '[data-agora-archive-infinite-loading]',
    liveRegion: '[data-agora-archive-live-region]',
    bar: '[data-agora-bar]',
    barToggle: '[data-agora-bar-toggle]',
    barDropdown: '[data-agora-bar-dropdown]',
    barBackdrop: '[data-agora-bar-backdrop]',
    activeFiltersContainer: '[data-agora-archive-active-filters]',
    tagRemove: '[data-agora-tag-remove]',
    headerContainer: '[data-agora-archive-header]',
};

// Module-level observer state. Moet losstaan per-init cycle zodat
// filter/sort change de vorige observer kan disconnecten zonder memory
// leak. `autoLoadsDone` houdt de teller bij over volgende DOM updates
// tot filter/sort reset.
let activeInfiniteObserver = null;
let autoLoadsDone = 0;

// Panel-state (drawer of modal) wordt bewaard over AJAX-updates: de
// toggle-button staat buiten de filters-container en
// overleeft de swap — zijn `aria-expanded` is source-of-truth. We
// cachen de state vóór updateDOM zodat we kunnen restoren als het
// panel in de fresh render standaard hidden rendert.
let panelWasOpen = false;

// Panel-configs: drawer en modal delen de generieke
// helpers maar krijgen eigen selector-set + overlay-beleid. Modal is
// ALTIJD overlay; drawer alleen bij overlay-mode of responsive+mobile.
const DRAWER_SELECTORS = {
    toggle: '[data-agora-drawer-toggle]',
    panel: '[data-agora-drawer]',
    close: '[data-agora-drawer-close]',
    backdrop: '[data-agora-drawer-backdrop]',
    badge: '[data-agora-drawer-badge]',
    boundAttr: 'agoraDrawerBound',
    dialogSelector: '.archive-drawer__panel',
};
const MODAL_SELECTORS = {
    toggle: '[data-agora-modal-toggle]',
    panel: '[data-agora-modal]',
    close: '[data-agora-modal-close]',
    backdrop: '[data-agora-modal-backdrop]',
    badge: '[data-agora-modal-badge]',
    boundAttr: 'agoraModalBound',
    dialogSelector: '.archive-modal__dialog',
};
const DRAWER_CONFIG = { selectors: DRAWER_SELECTORS, alwaysOverlay: false };
const MODAL_CONFIG = { selectors: MODAL_SELECTORS, alwaysOverlay: true };

// Debounce timer voor instant-filter mode. Eén timer per
// pagina-instantie omdat er maximaal één archive-form is.
let instantDebounceTimer = null;

// Actieve focus-trap voor panel overlay (drawer of modal). Alleen
// actief wanneer panel open staat in overlay-variant; bij close of
// niet-overlay-expand wordt hij gedeactiveerd en op null gezet.
let activePanelFocusTrap = null;

const REST_BASE = '/wp-json/agora/v1/archive';

/**
 * Parse een URL naar een state-object met pathname + params.
 *
 * @param {string} url
 * @returns {{ baseUrl: string, params: Record<string, string> }}
 */
export function parseUrl(url) {
    const urlObj = new URL(url, window.location.origin);
    const params = Object.fromEntries(urlObj.searchParams.entries());
    return {
        baseUrl: urlObj.pathname,
        params,
    };
}

/**
 * Bouw een schone URL uit een state-object.
 *
 * @param {{ params?: Record<string, string> }} state
 * @param {string} baseUrl
 * @param {Record<string, string>} [defaults]
 * @returns {string}
 */
export function buildUrl(state, baseUrl, defaults = {}) {
    const url = new URL(baseUrl, window.location.origin);

    for (const [key, value] of Object.entries(state.params || {})) {
        if (value === '' || value === null || value === undefined) continue;
        if (defaults[key] !== undefined && value === defaults[key]) continue;
        // loaded=1 (of lager) is default state → niet in URL.
        if (key === 'loaded' && Number(value) <= 1) continue;
        url.searchParams.set(key, value);
    }

    // Pretty-URL pagination cleanup:
    //  - Geen `paged` in state (filter/sort change) → strip ELKE `/page/N/`
    //    suffix zodat we altijd naar pagina 1 resetten, ook als baseUrl
    //    initieel `/references/page/2/` was.
    //  - Met `paged` in state (pagination click via handleAjaxNavigate, of
    //    form-submit waarbij paged expliciet bewaard wordt) → alleen `/page/1/`
    //    strippen; andere pages horen in de URL.
    const hasPaged = !!(state.params && state.params.paged);
    url.pathname = hasPaged
        ? url.pathname.replace(/\/page\/1\/?$/, '/')
        : url.pathname.replace(/\/page\/\d+\/?$/, '/');

    // Komma's zijn veilige sub-delimiters in query strings (RFC 3986).
    // URL API encodeert ze onnodig naar %2C — decodeer terug voor
    // leesbare URLs bij checkbox multi-select filters.
    return url.toString().replace(/%2C/gi, ',');
}

/**
 * Map een frontend URL naar de REST endpoint URL.
 *
 * @param {string} frontendUrl
 * @param {string} archiveType
 * @returns {string}
 */
function mapUrlToRestUrl(frontendUrl, archiveType) {
    const url = new URL(frontendUrl, window.location.origin);
    const restUrl = new URL(REST_BASE, window.location.origin);

    restUrl.searchParams.set('archive_type', archiveType);
    for (const [key, value] of url.searchParams.entries()) {
        restUrl.searchParams.set(key, value);
    }
    // Pretty-URL /page/N/ → paged query param.
    const pageMatch = url.pathname.match(/\/page\/(\d+)\/?$/);
    if (pageMatch && !restUrl.searchParams.has('paged')) {
        restUrl.searchParams.set('paged', pageMatch[1]);
    }
    return restUrl.toString();
}

function getFormState(form) {
    const params = {};
    if (!form) return { params };
    const data = new FormData(form);
    for (const [key, value] of data.entries()) {
        if (typeof value === 'string') params[key] = value;
    }
    return { params };
}

function getContainers() {
    return {
        posts: document.querySelector(SELECTORS.postsContainer),
        pagination: document.querySelector(SELECTORS.paginationContainer),
        filters: document.querySelector(SELECTORS.filtersContainer),
        activeFilters: document.querySelector(SELECTORS.activeFiltersContainer),
        header: document.querySelector(SELECTORS.headerContainer),
    };
}

function updateDOM(data, options = {}) {
    const { posts, pagination, filters, activeFilters, header } = getContainers();
    if (posts && typeof data.html === 'string') {
        posts.innerHTML = data.html;
    }
    if (pagination && typeof data.pagination === 'string') {
        pagination.innerHTML = data.pagination;
    }
    if (!options.skipFilters && filters && typeof data.filters === 'string') {
        filters.innerHTML = data.filters;
    }
    // Active filter tags: aparte container die altijd
    // bestaat, ook als hij leeg gerenderd wordt. Update onafhankelijk
    // van `skipFilters` zodat de tags ook bij bar instant-mode bij
    // werken (de filter-container blijft dan intact, maar de tags
    // zijn een andere container).
    if (activeFilters && typeof data.active_filters === 'string') {
        activeFilters.innerHTML = data.active_filters;
    }
    // Archive header: titel kan wijzigen bij bv.
    // search-term updates ("Zoekresultaten voor: foo" → "… bar").
    // Container blijft altijd in DOM; innerHTML wordt vervangen.
    if (header && typeof data.header === 'string') {
        header.innerHTML = data.header;
    }

    if (typeof data.active_filter_count === 'number') {
        // Update badge op welk panel-type actief is; de andere is een
        // no-op (selector matcht niks). Eén van beide is in DOM, nooit
        // beide tegelijk per pagina.
        updateDrawerBadge(data.active_filter_count);
        updateModalBadge(data.active_filter_count);
    }

    // Re-enhance native selects die via AJAX in de filters-container
    // zijn verschenen. `initCustomSelects` is idempotent — al-enhanced
    // selects worden geskipt via `data-agora-select-enhanced`.
    initCustomSelects();
    // NB: drawer open/dicht state wordt NIET in updateDOM zelf gezet.
    // Elke caller beslist: `handleActionAjax` sluit (filter apply is klaar),
    // `handleAjaxNavigate` behoudt state (pagination/reset), `handlePopstate`
    // sluit (schone start bij browser back/forward).
}

/**
 * Strip `/page/1/` suffix uit een URL — pagina 1 is altijd de base URL.
 * Werkt voor zowel `/references/page/1/` als `/references/page/1/?foo=bar`.
 *
 * @param {string} url
 * @returns {string}
 */
export function cleanPageOneFromUrl(url) {
    return url.replace(/\/page\/1\/(?=\?|$)/, '/');
}

/**
 * True wanneer URL een same-origin niet-REST URL is (dus bruikbaar voor
 * AJAX-navigatie via het REST endpoint).
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isArchiveUrl(url) {
    try {
        const urlObj = new URL(url, window.location.origin);
        if (urlObj.origin !== window.location.origin) return false;
        if (urlObj.pathname.startsWith('/wp-json/')) return false;
        return true;
    } catch {
        return false;
    }
}

function setLoadingState(loading, options = {}) {
    const { posts } = getContainers();
    if (posts) {
        posts.dataset.agoraArchiveLoading = loading ? 'true' : 'false';
    }

    // Skip form control disabling bij pagination (filters veranderen niet)
    if (options.skipControls) return;

    const form = document.querySelector(SELECTORS.form);
    if (form && form.id) {
        // Controls binnen het form (leeg in Pattern C) + controls die via
        // form-attribuut aan dit form hangen.
        const selector = `[form="${form.id}"]`;
        document.querySelectorAll(selector).forEach((c) => {
            c.disabled = loading;
        });
    }
}

async function fetchArchive(cleanUrl, archiveType) {
    const restUrl = mapUrlToRestUrl(cleanUrl, archiveType);
    const response = await fetch(restUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
}

/**
 * Sync form-control values met de huidige URL. Gebruikt door popstate
 * zodat select-waarden weer kloppen na een browser back/forward.
 */
function syncFormFromUrl(form, url) {
    if (!form || !form.id) return;
    const urlObj = new URL(url, window.location.origin);
    const params = urlObj.searchParams;

    const controls = document.querySelectorAll(`[form="${form.id}"]`);
    controls.forEach((control) => {
        if (!control.name || !('value' in control)) return;

        // Radio, checkbox en hidden inputs zijn server-rendered met
        // correcte values en checked state. syncFormFromUrl mag hun
        // value attribuut niet overschrijven — dat zou radio values
        // destructief leegmaken.
        if (control.type === 'radio' || control.type === 'checkbox' || control.type === 'hidden') {
            return;
        }

        const urlValue = params.get(control.name);
        if (urlValue !== null) {
            control.value = urlValue;
            return;
        }

        // URL bevat de param niet → controls met explicit default
        // (zoals de sort-select via `data-default-sort`) springen terug
        // naar hun default. Andere controls deselecteren (leeg).
        if (control.dataset && typeof control.dataset.defaultSort === 'string') {
            control.value = control.dataset.defaultSort;
        } else {
            control.value = '';
        }
    });
}

async function handleActionAjax(form, baseUrl, defaults, options = {}) {
    const state = getFormState(form);
    // Filter- of sort-wijziging reset pagination naar pagina 1 (replace mode).
    // Pagination link clicks lopen via `handleAjaxNavigate` en behouden paged
    // juist wél. Hetzelfde voor load-more state.
    delete state.params.paged;
    delete state.params.loaded;

    const cleanUrl = buildUrl(state, baseUrl, defaults);
    const archiveType = form.dataset.archiveType;

    if (!archiveType) {
        // Zonder archive_type kunnen we het REST endpoint niet aanroepen:
        // val terug op page reload met schone URL.
        window.location.href = cleanUrl;
        return;
    }

    setLoadingState(true);
    try {
        const data = await fetchArchive(cleanUrl, archiveType);

        if (options.closeDrawer === false) {
            preserveDrawerState();
        }

        // Forward `skipFilters` (bar instant-mode) naar updateDOM zodat
        // de filters-container intact blijft terwijl posts + pagination
        // wel updaten.
        updateDOM(data, { skipFilters: options.skipFilters === true });
        history.pushState({ ajax: true, url: cleanUrl }, '', cleanUrl);

        // Filter/sort apply is "klaar"-actie → actief panel (drawer of
        // modal) dichtdoen zodat gebruiker het filtered resultaat ziet.
        // Overlay-body-scroll-lock wordt door het sluiten ook opgeheven.
        if (options.closeDrawer !== false) {
            const cfg = getActivePanelConfig();
            if (cfg) _setPanelState(false, cfg);
        } else {
            restoreActivePanelState();
        }

        // Filter/sort reset: nieuwe pagination container, auto-loads
        // counter terug naar 0 zodat de observer opnieuw max_auto_loads
        // cycles mag doen.
        reinitAfterDomUpdate(archiveType, { resetAutoLoads: true });
    } catch (error) {
        console.error('Archive AJAX failed:', error);
        window.location.href = cleanUrl;
        return;
    } finally {
        setLoadingState(false);
    }
}

/**
 * Navigeer naar een archive URL via AJAX (gebruikt door
 * pagination-click intercept en door popstate). Behoudt `paged` in de
 * URL — dit is bewust anders dan `handleActionAjax` die juist paged
 * resetten bij filter/sort wijzigingen.
 *
 * @param {string} url
 * @param {string} archiveType
 * @param {{ pushState?: boolean }} [options]
 */
async function handleAjaxNavigate(url, archiveType, options = {}) {
    const pushState = options.pushState !== false;
    // `/page/1/` is de default state — stript uit zowel de URL die we
    // fetchen als die we pushen. Dit zorgt dat een klik op "pagina 1"
    // van de numeric pagination /references/ oplevert in de address bar,
    // niet /references/page/1/.
    const cleanUrl = cleanPageOneFromUrl(url);
    const form = document.querySelector(SELECTORS.form);

    setLoadingState(true, { skipControls: options.skipFilters });
    try {
        // Pagination/reset click: drawer state behouden. Als user de
        // drawer open heeft, verwacht hij hem nog open te zien na een
        // page-navigatie binnen het archief.
        preserveDrawerState();
        const data = await fetchArchive(cleanUrl, archiveType);
        updateDOM(data, { skipFilters: options.skipFilters });
        if (pushState) {
            history.pushState({ ajax: true, url: cleanUrl }, '', cleanUrl);
        }
        if (form) {
            syncFormFromUrl(form, cleanUrl);
        }
        restoreDrawerState();
        // Pagination link klik of popstate: pagination container is
        // vernieuwd; observer-state niet resetten (user behoudt zijn
        // accumulated auto-loads counter bij terug/vooruit navigatie).
        reinitAfterDomUpdate(archiveType);
    } catch (error) {
        console.error('Archive AJAX navigate failed:', error);
        window.location.href = cleanUrl;
        return;
    } finally {
        setLoadingState(false);
    }
}

/**
 * Generieke event-delegation op een container: klik op een archive-link
 * binnen de container triggert AJAX-navigatie in plaats van page reload.
 *
 * Werkt voor pagination én voor filter-containers (reset-link): alleen de
 * kinderen van de container worden via innerHTML vervangen bij AJAX
 * updates, de container zelf blijft staan — dus de listener overleeft
 * alle updates.
 *
 * @param {string} selector   CSS selector voor de container
 * @param {string} archiveType
 */
function initLinkIntercept(selector, archiveType, options = {}) {
    const container = document.querySelector(selector);
    if (!container) return;

    container.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        if (!isArchiveUrl(link.href)) return;
        e.preventDefault();
        handleAjaxNavigate(link.href, archiveType, { skipFilters: options.skipFilters }).then(
            () => {
                // `closeDrawer: true` = "sluit indien gepast";
                // `shouldCloseDrawer()` = "is sluiten gepast voor huidige mode/viewport?".
                // Panel-agnostic: sluit drawer of modal afhankelijk
                // van welk type in DOM staat.
                if (options.closeDrawer && shouldCloseActivePanel()) {
                    const cfg = getActivePanelConfig();
                    if (cfg) _setPanelState(false, cfg);
                }
            },
        );
    });
}

// =============================================================================
// Load-more pagination
// =============================================================================

/**
 * Bouwt een URL voor de huidige location met `?loaded=N` gezet (of
 * verwijderd als N <= 1).
 *
 * @param {number} loaded
 * @returns {string}
 */
function buildLoadedUrl(loaded) {
    const url = new URL(window.location.href);
    if (!loaded || loaded <= 1) {
        url.searchParams.delete('loaded');
    } else {
        url.searchParams.set('loaded', String(loaded));
    }
    return url.toString();
}

/**
 * Klik op load-more button: fetch volgende pagina via `paged=N` (single
 * page), append posts, vervang pagination + filters, push `?loaded=N`.
 *
 * Paged wordt hier gebruikt om één pagina per fetch op te halen — de
 * `loaded` param is puur URL-state. Bij een popstate of refresh met
 * `?loaded=N` wordt het REST endpoint wél met `loaded=N` aangeroepen om
 * alle pages 1..N in één response te krijgen.
 *
 * @param {HTMLElement} triggerElement  Element met `data-next-page` (button of container)
 * @param {string} archiveType
 * @param {{ silentFail?: boolean }} [options]
 *   `silentFail=true` voorkomt `window.location.reload()` bij fetch errors —
 *   gebruikt door infinite-scroll flow die zelf wil beslissen wat te doen
 *   (bv. alleen loading indicator verbergen en observer laten doorlopen).
 */
async function handleLoadMore(triggerElement, archiveType, options = {}) {
    const silentFail = options.silentFail === true;
    const nextPage = parseInt(triggerElement.dataset.nextPage || '', 10);
    if (Number.isNaN(nextPage) || nextPage < 2) return;

    setLoadingState(true);
    try {
        const restUrl = new URL(REST_BASE, window.location.origin);
        restUrl.searchParams.set('archive_type', archiveType);
        restUrl.searchParams.set('paged', String(nextPage));

        // Bestaande filter/sort params uit URL meenemen (niet loaded).
        const currentParams = new URL(window.location.href).searchParams;
        for (const [key, value] of currentParams.entries()) {
            if (key === 'loaded' || key === 'paged') continue;
            restUrl.searchParams.set(key, value);
        }

        const response = await fetch(restUrl.toString(), {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        const { posts, pagination } = getContainers();
        // APPEND (niet vervangen) voor load-more — dat is de hele point.
        // Filters-container wordt bewust NIET gerefreshed: bij load-more
        // verandert de filter-state niet, alleen het aantal posts groeit.
        if (posts && typeof data.html === 'string') {
            posts.insertAdjacentHTML('beforeend', data.html);
        }
        if (pagination && typeof data.pagination === 'string') {
            pagination.innerHTML = data.pagination;
        }

        const loaded = nextPage;
        const stateUrl = buildLoadedUrl(loaded);
        history.pushState({ ajax: true, url: stateUrl, loaded }, '', stateUrl);
    } catch (error) {
        console.error('Archive load-more AJAX failed:', error);
        if (!silentFail) {
            window.location.reload();
        }
        throw error;
    } finally {
        setLoadingState(false);
    }
}

/**
 * Event delegation op pagination container voor load-more buttons.
 * Container zelf blijft; innerHTML vervanging wist de button maar de
 * listener op container overleeft updates.
 *
 * @param {string} archiveType
 */
// =============================================================================
// Infinite scroll
// =============================================================================

/**
 * Meldt nieuwe content aan screen readers via de `aria-live` region die
 * de infinite-scroll partial rendert. Kort en informatief; text wordt
 * telkens opnieuw gezet zodat AT's het als update oppakken.
 */
function announceNewContent() {
    const liveRegion = document.querySelector(SELECTORS.liveRegion);
    const postsContainer = document.querySelector(SELECTORS.postsContainer);
    if (!liveRegion || !postsContainer) return;
    const count = postsContainer.children.length;
    liveRegion.textContent = `${count} resultaten zichtbaar`;
}

/**
 * Zet een Intersection Observer op de sentinel op. Wanneer sentinel in
 * viewport komt, triggert `handleLoadMore` met `silentFail=true` (zodat
 * een fetch-fout niet in een reload-loop belandt). Na `max_auto_loads`
 * cycles verbergt de sentinel en verschijnt de fallback button — die
 * heeft `data-agora-archive-load-more` dus wordt opgepakt door
 * `initLoadMoreIntercept`.
 *
 * @param {string} archiveType
 */
function initInfiniteScrollIntercept(archiveType) {
    // Disconnect vorige observer om memory leaks te voorkomen (filter/
    // sort change vervangt de container-HTML; state leeft in module
    // scope zodat we de oude observer kunnen opruimen).
    if (activeInfiniteObserver) {
        activeInfiniteObserver.disconnect();
        activeInfiniteObserver = null;
    }

    const container = document.querySelector(SELECTORS.infiniteScrollContainer);
    if (!container) return;

    const sentinel = container.querySelector(SELECTORS.sentinel);
    if (!sentinel) return;

    const threshold = parseInt(container.dataset.threshold || '200', 10);
    const maxAutoLoads = parseInt(container.dataset.maxAutoLoads || '3', 10);

    let isLoadingMore = false;

    const observer = new IntersectionObserver(
        (entries) => {
            if (!entries[0] || !entries[0].isIntersecting) return;
            if (isLoadingMore) return;

            // Max auto-loads bereikt: stop observer, toon fallback button.
            // De button heeft data-agora-archive-load-more → wordt opgepakt
            // door initLoadMoreIntercept; geen aparte handler nodig.
            if (autoLoadsDone >= maxAutoLoads) {
                observer.disconnect();
                const fresh = document.querySelector(SELECTORS.infiniteScrollContainer);
                if (fresh) {
                    const freshSentinel = fresh.querySelector(SELECTORS.sentinel);
                    const freshFallback = fresh.querySelector(SELECTORS.loadMoreButton);
                    if (freshSentinel) freshSentinel.hidden = true;
                    if (freshFallback) freshFallback.hidden = false;
                }
                return;
            }

            // Trigger data komt uit het container element; nextPage /
            // maxPages staan als data-attributes op dezelfde container.
            const currentContainer = document.querySelector(SELECTORS.infiniteScrollContainer);
            if (!currentContainer) return;
            const nextPage = parseInt(currentContainer.dataset.nextPage || '', 10);
            const maxPages = parseInt(currentContainer.dataset.maxPages || '', 10);
            if (Number.isNaN(nextPage) || nextPage > maxPages) {
                observer.disconnect();
                return;
            }

            isLoadingMore = true;
            const loadingEl = currentContainer.querySelector(SELECTORS.infiniteLoading);
            if (loadingEl) loadingEl.hidden = false;

            handleLoadMore(currentContainer, archiveType, { silentFail: true })
                .then(() => {
                    autoLoadsDone += 1;
                    announceNewContent();
                    // Pagination container innerHTML is vervangen door
                    // handleLoadMore — de OUDE sentinel bestaat niet meer.
                    // Re-init zet een verse observer op de nieuwe sentinel
                    // (of stopt als er geen meer is / max_auto_loads bereikt).
                    reinitAfterDomUpdate(archiveType);
                })
                .catch(() => {
                    // Error log gebeurt in handleLoadMore. Hier geen reload
                    // omdat we via silentFail zijn binnengekomen; observer
                    // mag nog doorlopen voor de volgende intersect.
                    isLoadingMore = false;
                    const el = document.querySelector(SELECTORS.infiniteLoading);
                    if (el) el.hidden = true;
                });
        },
        { rootMargin: `${threshold}px 0px` },
    );

    observer.observe(sentinel);
    activeInfiniteObserver = observer;
}

/**
 * Roept na elke DOM-update (filter submit, sort change, pagination
 * klik, popstate) `initInfiniteScrollIntercept` opnieuw aan wanneer de
 * nieuwe DOM een infinite-scroll container bevat. Filter/sort reset
 * zet ook `autoLoadsDone` terug op 0.
 *
 * @param {string} archiveType
 * @param {{ resetAutoLoads?: boolean }} [options]
 */
function reinitAfterDomUpdate(archiveType, options = {}) {
    if (options.resetAutoLoads === true) {
        autoLoadsDone = 0;
    }
    const infinite = document.querySelector(SELECTORS.infiniteScrollContainer);
    if (infinite) {
        initInfiniteScrollIntercept(archiveType);
    } else if (activeInfiniteObserver) {
        // Geen container meer (bv. einde bereikt of ander type) — oude
        // observer opruimen.
        activeInfiniteObserver.disconnect();
        activeInfiniteObserver = null;
    }
}

// =============================================================================
// Drawer filter layout
// =============================================================================

/**
 * Zet drawer open of dicht. Synct `aria-expanded` op toggle en `hidden`
 * op drawer. Lockt body-scroll via `lockScroll()` bij overlay-varianten
 * zodat scrollen achter de drawer niet werkt — `lockScroll()` meet de
 * scrollbar-breedte en compenseert met `padding-right` zodat content
 * niet verspringt op desktops met traditionele scrollbars.
 *
 * @param {boolean} open
 */
/**
 * Debounced wrapper rond `handleActionAjax` voor instant-filter mode.
 * Clear de vorige timer zodat snel achter elkaar wijzigen (bv. meerdere
 * checkboxes) maar één AJAX-request oplevert. Debounce-waarde komt uit
 * `data-debounce` op het archive-form (default 300ms).
 *
 * @param {HTMLFormElement} form
 * @param {string} baseUrl
 * @param {Record<string, string>} defaults
 */
function handleInstantFilter(form, baseUrl, defaults) {
    if (instantDebounceTimer !== null) {
        clearTimeout(instantDebounceTimer);
    }
    const debounceMs = parseInt(form.dataset.debounce || '300', 10);
    instantDebounceTimer = setTimeout(
        async () => {
            instantDebounceTimer = null;
            // Bar-dropdown die open staat onthouden vóór de fetch — de
            // filters-container wordt door de AJAX-update vervangen,
            // dus de oude dropdown verdwijnt. Door de filters wél te
            // laten vervangen krijgen we actuele tags-bar; daarna
            // heropenen we dezelfde dropdown in de verse DOM.
            const openDropdownId = getOpenBarDropdownId();
            await handleActionAjax(form, baseUrl, defaults, {
                closeDrawer: shouldCloseActivePanel(),
            });
            if (openDropdownId) {
                reopenBarDropdown(openDropdownId);
            }
        },
        Number.isFinite(debounceMs) && debounceMs >= 0 ? debounceMs : 300,
    );
}

/**
 * Bepaal of de drawer gesloten moet worden na een filter/sort-actie of
 * link-klik binnen de filters-container. Sluit alleen wanneer de drawer
 * als overlay verschijnt (altijd-overlay-mode, of responsive-mode op
 * mobile). Op desktop in responsive-mode blijft de drawer open zodat
 * gebruikers verder kunnen verfijnen zonder opnieuw te openen.
 *
 * @returns {boolean}
 */
// =============================================================================
// Generiek panel-systeem
// =============================================================================
// Drawer en modal delen dezelfde interne logica via config-driven helpers.
// Backwards-compat: oude `setDrawerState()` / `initDrawer()` / enz. blijven
// beschikbaar als thin wrappers — geen child theme breakage.

/**
 * True wanneer het panel scroll-lock + focus-trap nodig heeft. Modal is
 * altijd overlay; drawer alleen bij `drawerMode=overlay` of
 * `responsive` op mobile viewport.
 */
function _shouldLockForPanel(panel, alwaysOverlay) {
    if (alwaysOverlay) return true;
    const mode = panel.dataset.drawerMode || 'responsive';
    return mode === 'overlay' || (mode === 'responsive' && window.innerWidth < 768);
}

/**
 * Open/sluit een panel (drawer of modal) met Motion.js animatie bij
 * overlay-varianten. Non-overlay (desktop drawer expand) blijft
 * instant — daar geeft een animatie geen meerwaarde.
 *
 * @param {boolean} open
 * @param {{selectors: object, alwaysOverlay: boolean}} config
 * @param {{skipAnimation?: boolean}} [options]  AJAX-restore zet
 *        `skipAnimation: true` zodat het panel instant in de juiste
 *        state staat zonder visuele afleiding.
 *
 * @returns {Promise<void>}
 */
async function _setPanelState(open, config, options = {}) {
    const { selectors, alwaysOverlay } = config;
    const toggle = document.querySelector(selectors.toggle);
    const panel = document.querySelector(selectors.panel);
    if (!toggle || !panel) return;

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    const isOverlay = _shouldLockForPanel(panel, alwaysOverlay);
    const skipAnimation = options.skipAnimation === true;
    const dialog = panel.querySelector(selectors.dialogSelector) || panel;
    const backdrop = panel.querySelector(selectors.backdrop);

    if (open) {
        panel.setAttribute('aria-hidden', 'false');

        if (isOverlay) {
            panel.hidden = false;
            // `lockScroll()` zet `overflow: hidden` op `<body>` én
            // compenseert de verdwijnende scrollbar-breedte met
            // `padding-right` zodat de pagina-content niet verspringt
            // op desktops met traditionele scrollbars. Mobile-nav
            // gebruikt dezelfde shared utility — nested locks worden
            // door de counter in `scroll-lock.js` correct afgehandeld.
            lockScroll();

            if (!skipAnimation) {
                if (backdrop instanceof HTMLElement) {
                    fadeIn(backdrop, { duration: 0.2 });
                }
                if (dialog instanceof HTMLElement) {
                    await _animatePanelIn(dialog, config, panel);
                }
            } else if (dialog instanceof HTMLElement) {
                // Restore-flow zet eindstate direct — geen animatie maar
                // wel visueel correct (anders blijft de initial transform
                // uit de SCSS hangen).
                _resetPanelInlineState(dialog, backdrop);
            }

            // Focus-trap NA de animatie zodat focus niet op een nog uit-
            // beeld element terechtkomt. Close-button krijgt initial
            // focus (ARIA dialog-pattern) — volgende Tab navigeert naar
            // filters.
            if (dialog instanceof HTMLElement) {
                if (activePanelFocusTrap) {
                    activePanelFocusTrap.deactivate();
                }
                activePanelFocusTrap = createFocusTrap(dialog);
                const closeButton = dialog.querySelector(selectors.close);
                activePanelFocusTrap.activate(
                    closeButton instanceof HTMLElement ? closeButton : null,
                );
            }
        } else {
            // Non-overlay (desktop drawer expand): class-based toggle
            // i.p.v. hidden zodat CSS-transities werken. Twee-frame rAF
            // patroon consistent met custom select en bar dropdowns —
            // hidden weghalen, dan op het volgende frame `.is-open`
            // toevoegen zodat de transitie vanuit de gesloten-state start.
            //
            // Geen scroll-lock in deze branch: desktop expand laat de
            // pagina gewoon scrollbaar. Een eventuele stale lock van
            // een eerdere overlay-state wordt door de close-non-overlay
            // branch hieronder defensief opgeruimd.
            if (activePanelFocusTrap) {
                activePanelFocusTrap.deactivate();
                activePanelFocusTrap = null;
            }
            panel.removeAttribute('hidden');
            if (skipAnimation) {
                // AJAX-restore: eindstate direct zonder rAF-flash.
                panel.classList.add('is-open');
            } else {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        panel.classList.add('is-open');
                    });
                });
            }
        }
        return;
    }

    // --- CLOSE ---
    if (isOverlay) {
        // Focus-trap eerst weg zodat focus terug naar toggle gaat
        // vóór het panel wegschuift.
        if (activePanelFocusTrap) {
            activePanelFocusTrap.deactivate();
            activePanelFocusTrap = null;
        }

        if (!skipAnimation) {
            const animations = [];
            if (backdrop instanceof HTMLElement) {
                animations.push(fadeOut(backdrop, { duration: 0.2 }));
            }
            if (dialog instanceof HTMLElement) {
                animations.push(_animatePanelOut(dialog, config, panel));
            }
            await Promise.all(animations);
        }

        // Decrement de scroll-lock counter; bij counter = 0 wordt
        // `overflow` + originele `padding-right` op `<body>` hersteld.
        unlockScroll();
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
        return;
    }

    // Non-overlay close: `.is-open` weg → CSS fade/slide out → na de
    // transitie hidden zetten zodat :has()-grid-collapse triggert. Een
    // nieuwe open-call binnen het venster heropent zonder dat de
    // timeout hidden zet (guard op classList-check).
    //
    // Defensieve `unlockScroll()`: vangt de overlay→non-overlay
    // viewport-resize edge case op waarbij een vorige overlay-state
    // de body locked achterliet. `lockScroll`'s counter is op 0
    // geclamped, dus zonder eerdere lock is dit een no-op.
    unlockScroll();
    if (activePanelFocusTrap) {
        activePanelFocusTrap.deactivate();
        activePanelFocusTrap = null;
    }
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    setTimeout(() => {
        if (!panel.classList.contains('is-open')) {
            panel.setAttribute('hidden', '');
        }
    }, 250);
}

/**
 * Bepaal de slide-richting voor een drawer panel.
 *
 * Position zit als modifier op de `.archive` parent wrapper, niet op
 * het panel zelf. `archive--drawer-right` slidet van rechts;
 * `archive--drawer-left` (default) en `archive--drawer-top` sliden
 * beide van links — de top-variant is een grid-positie boven de
 * posts, maar als overlay/responsive panel komt hij van links.
 *
 * @param {HTMLElement} panel  Het drawer wrapper element.
 * @returns {'left'|'right'}
 */
function _getDrawerSlideDirection(panel) {
    const archiveWrapper = panel.closest('.archive');
    if (
        archiveWrapper &&
        archiveWrapper.classList.contains('archive--drawer-right')
    ) {
        return 'right';
    }
    return 'left';
}

/**
 * Animeer een panel in (open). Drawer slidet vanuit zijn position-
 * modifier (rechts of boven); modal scaled + faded in vanuit het
 * midden.
 *
 * @param {HTMLElement} dialog
 * @param {{selectors: object, alwaysOverlay: boolean}} config
 * @param {HTMLElement} wrapper
 *
 * @returns {Promise<unknown>}
 */
async function _animatePanelIn(dialog, config, wrapper) {
    if (config === MODAL_CONFIG) {
        await motionAnimate(
            dialog,
            {
                opacity: [0, 1],
                transform: ['scale(0.95)', 'scale(1)'],
            },
            { duration: 0.25, easing: 'ease-out' },
        );
        return;
    }

    const direction = _getDrawerSlideDirection(wrapper);
    const startValue = direction === 'left' ? '-100%' : '100%';

    await motionAnimate(
        dialog,
        { transform: [`translateX(${startValue})`, 'translateX(0)'] },
        { duration: 0.3, easing: 'ease-out' },
    );
}

/**
 * Animeer een panel uit (close). Reverse van `_animatePanelIn`.
 *
 * @param {HTMLElement} dialog
 * @param {{selectors: object, alwaysOverlay: boolean}} config
 * @param {HTMLElement} wrapper
 *
 * @returns {Promise<unknown>}
 */
async function _animatePanelOut(dialog, config, wrapper) {
    if (config === MODAL_CONFIG) {
        await motionAnimate(
            dialog,
            {
                opacity: [1, 0],
                transform: ['scale(1)', 'scale(0.95)'],
            },
            { duration: 0.2, easing: 'ease-in' },
        );
        return;
    }

    const direction = _getDrawerSlideDirection(wrapper);
    const endValue = direction === 'left' ? '-100%' : '100%';

    await motionAnimate(
        dialog,
        { transform: ['translateX(0)', `translateX(${endValue})`] },
        { duration: 0.25, easing: 'ease-in' },
    );
}

/**
 * Reset inline-styles op dialog + backdrop naar de visueel-actieve
 * state. Wordt door de restore-flow gebruikt na een AJAX update zodat
 * een open panel niet de initial-transform-fallback uit de SCSS
 * vasthoudt.
 *
 * @param {HTMLElement} dialog
 * @param {Element|null} backdrop
 */
function _resetPanelInlineState(dialog, backdrop) {
    dialog.style.opacity = '1';
    dialog.style.transform = 'none';
    if (backdrop instanceof HTMLElement) {
        backdrop.style.opacity = '1';
    }
}

function _updatePanelBadge(selectors, count) {
    const badge = document.querySelector(selectors.badge);
    if (!badge) return;
    const safe = Number.isFinite(count) ? count : 0;
    badge.textContent = String(safe);
    badge.hidden = safe === 0;
}

function _initPanel(config) {
    const { selectors } = config;
    const toggle = document.querySelector(selectors.toggle);
    if (!toggle || toggle.dataset[selectors.boundAttr] === '1') return;
    toggle.dataset[selectors.boundAttr] = '1';

    toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        _setPanelState(!isOpen, config);
    });

    document.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        if (e.target.closest(selectors.close)) {
            _setPanelState(false, config);
            toggle.focus();
            return;
        }
        if (e.target.closest(selectors.backdrop)) {
            _setPanelState(false, config);
            toggle.focus();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const t = document.querySelector(selectors.toggle);
        if (t && t.getAttribute('aria-expanded') === 'true') {
            _setPanelState(false, config);
            t.focus();
        }
    });
}

/**
 * Detecteer welk panel-type in de DOM aanwezig is. Per pagina is er
 * maximaal één; deze helper geeft de bijbehorende config voor alle
 * panel-agnostische flows (AJAX update, close-on-apply, popstate).
 *
 * @returns {{ selectors: object, alwaysOverlay: boolean }|null}
 */
function getActivePanelConfig() {
    if (document.querySelector(DRAWER_SELECTORS.panel)) return DRAWER_CONFIG;
    if (document.querySelector(MODAL_SELECTORS.panel)) return MODAL_CONFIG;
    return null;
}

/**
 * True wanneer het actieve panel bij een filter/sort-apply gesloten
 * moet worden. Modal sluit altijd (is altijd overlay). Drawer sluit
 * alleen bij overlay-variant (zie `_shouldLockForPanel`).
 */
function shouldCloseActivePanel() {
    const config = getActivePanelConfig();
    if (!config) return false;
    if (config.alwaysOverlay) return true;
    const panel = document.querySelector(config.selectors.panel);
    return panel ? _shouldLockForPanel(panel, false) : false;
}

function preserveActivePanelState() {
    const config = getActivePanelConfig();
    if (!config) {
        panelWasOpen = false;
        return;
    }
    const toggle = document.querySelector(config.selectors.toggle);
    panelWasOpen = toggle ? toggle.getAttribute('aria-expanded') === 'true' : false;
}

function restoreActivePanelState() {
    if (panelWasOpen) {
        const config = getActivePanelConfig();
        if (config) {
            // Restore na AJAX: instant zonder animatie zodat de fresh
            // DOM-render direct in de juiste state staat (panel was
            // open vóór de swap; we willen geen visuele "open"-flash
            // bij elke filter/sort wijziging).
            _setPanelState(true, config, { skipAnimation: true });
        }
    }
    panelWasOpen = false;
}

// -----------------------------------------------------------------------------
// Drawer- en modal-specifieke wrappers
// -----------------------------------------------------------------------------
//
// `setDrawerState` / `setModalState` / `shouldCloseDrawer` waren
// backwards-compat wrappers van vóór de panel-systeem refactor.
// Inmiddels gebruiken alle interne callers `_setPanelState(false, cfg)`
// + `getActivePanelConfig()` of `shouldCloseActivePanel()`. Externe
// consumers bestaan niet (deze module exporteert alleen `parseUrl`,
// `buildUrl`, `initArchiveUrl`). De wrappers hieronder worden wél nog
// aangeroepen — `update*Badge` vanuit `updateDOM`, `init*` vanuit
// `initArchiveUrl`, `preserve*State`/`restore*State` vanuit de AJAX
// flows.

function updateDrawerBadge(count) {
    _updatePanelBadge(DRAWER_SELECTORS, count);
}

function preserveDrawerState() {
    preserveActivePanelState();
}

function restoreDrawerState() {
    restoreActivePanelState();
}

function initDrawer() {
    _initPanel(DRAWER_CONFIG);
}

function updateModalBadge(count) {
    _updatePanelBadge(MODAL_SELECTORS, count);
}

function initModal() {
    _initPanel(MODAL_CONFIG);
}

// =============================================================================
// Bar filter layout
// =============================================================================
// Per-filter dropdown. Fundamenteel anders dan drawer/modal (geen modal
// overlay, meerdere onafhankelijke dropdowns). Event-delegation op
// `document` zodat de listener AJAX innerHTML-updates van de filters-
// container overleeft.

/**
 * Open een dropdown en positioneer hem binnen viewport.
 *
 * @param {HTMLElement} dropdown
 * @param {HTMLButtonElement} toggle
 */
function openBarDropdown(dropdown, toggle) {
    toggle.setAttribute('aria-expanded', 'true');
    // Twee-frame patroon: eerst `hidden` weg zodat het element in de
    // render-tree staat; daarna op het volgende frame `is-open` toevoegen
    // zodat de CSS-transitie van opacity/visibility/transform daadwerkelijk
    // start vanuit de hidden-state. Zonder de tweede rAF batcht de browser
    // beide style-mutaties en zien we geen animatie. Zelfde patroon als
    // `custom-select.js::openListbox`.
    dropdown.removeAttribute('hidden');
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            dropdown.classList.add('is-open');
        });
    });
    positionBarDropdown(dropdown);

    const backdrop = document.querySelector(SELECTORS.barBackdrop);
    if (backdrop) {
        backdrop.removeAttribute('hidden');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                backdrop.classList.add('is-open');
            });
        });
    }
}

/**
 * Sluit alle dropdowns in een bar-container. Roept zonder argument de
 * eerste bar in de DOM; met argument een specifieke container.
 *
 * @param {HTMLElement} [bar]
 */
function closeAllBarDropdowns(bar) {
    const root = bar || document.querySelector(SELECTORS.bar);
    if (!root) return;

    root.querySelectorAll(SELECTORS.barDropdown).forEach((dd) => {
        dd.classList.remove('is-open');
        // Reset inline positioning zodat volgende open met schone slate start.
        dd.style.removeProperty('top');
        dd.style.removeProperty('bottom');
        dd.style.removeProperty('left');
        dd.style.removeProperty('right');
        // `hidden` pas zetten ná de CSS-transitie zodat de fade-out
        // zichtbaar is. 200ms = transitie-duur (150ms) + safety-marge.
        // Een nieuwe open-call binnen het venster triggert opnieuw
        // `is-open` en deze guard laat de timeout no-op'en.
        setTimeout(() => {
            if (!dd.classList.contains('is-open')) {
                dd.setAttribute('hidden', '');
            }
        }, 200);
    });
    root.querySelectorAll(SELECTORS.barToggle).forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
    });

    const backdrop = document.querySelector(SELECTORS.barBackdrop);
    if (backdrop) {
        backdrop.classList.remove('is-open');
        setTimeout(() => {
            if (!backdrop.classList.contains('is-open')) {
                backdrop.setAttribute('hidden', '');
            }
        }, 200);
    }
}

/**
 * Viewport-aware positionering: valt de dropdown onder of rechts uit
 * het scherm, dan flip naar boven of rechts-uitgelijnd. Op mobile
 * (bottom-sheet via CSS) doet JS niks — de media query regelt het.
 *
 * @param {HTMLElement} dropdown
 */
function positionBarDropdown(dropdown) {
    dropdown.style.removeProperty('top');
    dropdown.style.removeProperty('bottom');
    dropdown.style.removeProperty('left');
    dropdown.style.removeProperty('right');

    // Mobile (< 768px) heeft position: fixed bottom-sheet via CSS.
    if (window.innerWidth < 768) return;

    const rect = dropdown.getBoundingClientRect();

    if (rect.bottom > window.innerHeight) {
        dropdown.style.top = 'auto';
        dropdown.style.bottom = 'calc(100% + 4px)';
    }
    if (rect.right > window.innerWidth) {
        dropdown.style.left = 'auto';
        dropdown.style.right = '0';
    }
}

/**
 * Init bar: event-delegation op document voor toggle clicks, click-
 * outside, Escape en backdrop. Overleeft AJAX innerHTML-updates van de
 * filters-container omdat listeners op document-niveau zitten.
 */
function initBar() {
    // Guard: alleen binden wanneer er initieel een bar in DOM staat.
    // Na AJAX kan een nieuwe bar verschijnen — document-level listeners
    // vinden hem dan automatisch via query-selector.
    if (!document.querySelector(SELECTORS.bar)) return;
    if (document.documentElement.dataset.agoraBarBound === '1') return;
    document.documentElement.dataset.agoraBarBound = '1';

    document.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;

        // Toggle click: open/close dropdown.
        const toggle = e.target.closest(SELECTORS.barToggle);
        if (toggle) {
            const bar = toggle.closest(SELECTORS.bar);
            if (!bar) return;
            const filterId = toggle.dataset.agoraBarToggle || '';
            const dropdown = bar.querySelector(
                `[data-agora-bar-dropdown="${CSS.escape(filterId)}"]`,
            );
            if (!dropdown) return;

            const isOpen = !dropdown.hidden;
            closeAllBarDropdowns(bar);
            if (!isOpen) openBarDropdown(dropdown, toggle);
            return;
        }

        // Backdrop click.
        if (e.target.closest(SELECTORS.barBackdrop)) {
            closeAllBarDropdowns();
            return;
        }

        // Click outside bar.
        if (!e.target.closest(SELECTORS.bar)) {
            closeAllBarDropdowns();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const bar = document.querySelector(SELECTORS.bar);
        if (!bar) return;
        const openToggle = bar.querySelector(`${SELECTORS.barToggle}[aria-expanded="true"]`);
        closeAllBarDropdowns(bar);
        if (openToggle instanceof HTMLElement) openToggle.focus();
    });
}

/**
 * Filter-ID van de momenteel open bar-dropdown, of `null` wanneer er
 * geen open is. Gebruikt door `handleInstantFilter` om de open-state
 * over een filters-container innerHTML-swap te tillen.
 *
 * @returns {string|null}
 */
function getOpenBarDropdownId() {
    const bar = document.querySelector(SELECTORS.bar);
    if (!bar) return null;
    const openToggle = bar.querySelector(
        `${SELECTORS.barToggle}[aria-expanded="true"]`,
    );
    return openToggle ? openToggle.dataset.agoraBarToggle || null : null;
}

/**
 * Heropen een bar-dropdown na een filters-container innerHTML swap.
 * De vorige dropdown is door de swap verdwenen — deze functie zoekt
 * de verse DOM elementen op en opent de dropdown opnieuw met de
 * standaard `openBarDropdown` flow (incl. positionering en backdrop).
 *
 * @param {string} filterId
 */
function reopenBarDropdown(filterId) {
    const bar = document.querySelector(SELECTORS.bar);
    if (!bar) return;
    const toggle = bar.querySelector(
        `[data-agora-bar-toggle="${CSS.escape(filterId)}"]`,
    );
    const dropdown = bar.querySelector(
        `[data-agora-bar-dropdown="${CSS.escape(filterId)}"]`,
    );
    if (toggle instanceof HTMLButtonElement && dropdown instanceof HTMLElement) {
        openBarDropdown(dropdown, toggle);
    }
}

/**
 * Verwerk klik op een actieve-filter-tag's remove-button. Werkt voor
 * alle filter-layouts — de logic leest data-attributen op het element
 * zelf en is niet bar-specifiek.
 *
 * Update de bijhorende form-control(s) en triggert dan een AJAX
 * filter/sort flow die de tags-container opnieuw rendert.
 *
 * Radio: selecteer de "Alle" (value="") optie.
 * Checkbox: uncheck de specifieke checkbox én verwijder de waarde uit
 *   de hidden collector-input (komma-lijst).
 * Andere controls (defensief): zet value leeg.
 *
 * @param {HTMLElement} removeBtn
 * @param {HTMLFormElement} form
 * @param {string} baseUrl
 * @param {Record<string, string>} defaults
 */
async function handleTagRemove(removeBtn, form, baseUrl, defaults) {
    const filterId = removeBtn.dataset.filterId || '';
    const filterValue = removeBtn.dataset.filterValue || '';
    if (!filterId) return;

    const selector = `[form="${form.id}"][name="${CSS.escape(filterId)}"]`;
    const controls = document.querySelectorAll(selector);

    controls.forEach((control) => {
        if (!(control instanceof HTMLElement)) return;

        // Radio: "Alle"-optie (value="") aan; andere uit.
        if (control instanceof HTMLInputElement && control.type === 'radio') {
            control.checked = control.value === '';
            return;
        }

        // Hidden checkbox-collector: verwijder waarde uit komma-lijst
        // én uncheck de corresponderende checkbox zodat UI synchroon blijft.
        if (
            control instanceof HTMLInputElement &&
            control.type === 'hidden' &&
            control.dataset.agoraCheckboxCollector
        ) {
            const current = (control.value || '').split(',').filter((v) => v !== '');
            control.value = current.filter((v) => v !== filterValue).join(',');
            const checkbox = document.querySelector(
                `[data-agora-checkbox-item="${CSS.escape(filterId)}"][value="${CSS.escape(filterValue)}"]`,
            );
            if (checkbox instanceof HTMLInputElement) {
                checkbox.checked = false;
            }
            return;
        }

        // Fallback (bv. select): value leeg.
        if ('value' in control) {
            /** @type {HTMLInputElement} */ (control).value = '';
        }
    });

    // Trigger AJAX. `closeDrawer: false` — een tag-klik komt vanuit
    // de active-filters bar (buiten drawer/modal); we sluiten geen
    // panel. `skipFilters: false` zodat ook drawer/modal/inline/sidebar
    // hun filter UI bijwerken (in bar-mode laat de filters-container
    // dezelfde toggles zien — innerHTML-swap is goedkoop).
    await handleActionAjax(form, baseUrl, defaults, {
        closeDrawer: false,
        skipFilters: false,
    });

    // De DOM is na de AJAX-response vernieuwd; de zojuist geklikte
    // tag bestaat niet meer. Zet focus op een logisch volgend element
    // zodat keyboard-gebruikers niet naar `<body>` worden teruggegooid.
    focusAfterTagRemove();
}

/**
 * Zet focus op een logisch element na het verwijderen van een
 * active filter tag.
 *
 * Prioriteit:
 *  1. Eerstvolgende tag remove-button (zelfde of andere filter).
 *  2. Eerste filter-control in het archive-form (`[form="..."]
 *     [name]:not([type="hidden"])`) — typisch de eerste select of
 *     radio in de filters-container.
 *
 * Wanneer geen van beide gevonden wordt blijft focus waar hij is
 * (browser default na DOM-update is `<body>`, wat dan acceptabel is).
 */
function focusAfterTagRemove() {
    const remaining = document.querySelectorAll(SELECTORS.tagRemove);
    if (remaining.length > 0 && remaining[0] instanceof HTMLElement) {
        remaining[0].focus();
        return;
    }

    const form = document.querySelector(SELECTORS.form);
    if (!form || !form.id) return;

    const firstControl = document.querySelector(
        `[form="${form.id}"][name]:not([type="hidden"])`,
    );
    if (firstControl instanceof HTMLElement) {
        firstControl.focus();
    }
}

// =============================================================================
// Checkbox filter synchronisatie
// =============================================================================

/**
 * Synchroniseert aangevinkte checkboxes naar hun hidden-input
 * "collector" als komma-separated value. Pattern:
 *
 *   <input type="hidden" name="tag" data-agora-checkbox-collector="tag">
 *   <input type="checkbox" data-agora-checkbox-item="tag" value="a">
 *   <input type="checkbox" data-agora-checkbox-item="tag" value="b">
 *
 * Checkboxes hebben bewust geen `name` attribuut — alleen de collector
 * zit in het form (via `form="archive-form"`). `FormData` leest dus
 * exclusief de komma-string: `?tag=a,b`. Server-side
 * `TaxonomyFilter::apply_to_query_args()` splits die weer via
 * `get_active_values()` naar een IN-clause.
 *
 * Event delegation op `document` → werkt door over filters-container
 * `innerHTML` updates (AJAX filter refresh vervangt de checkboxes,
 * de listener blijft actief).
 */
function initCheckboxSync() {
    document.addEventListener('change', (e) => {
        const checkbox = e.target;
        if (!(checkbox instanceof HTMLInputElement)) return;
        if (checkbox.type !== 'checkbox') return;

        const filterId = checkbox.dataset.agoraCheckboxItem;
        if (!filterId) return;

        const collector = document.querySelector(
            `[data-agora-checkbox-collector="${CSS.escape(filterId)}"]`,
        );
        if (!collector) return;

        const items = document.querySelectorAll(
            `[data-agora-checkbox-item="${CSS.escape(filterId)}"]`,
        );
        const values = [];
        items.forEach((cb) => {
            if (cb instanceof HTMLInputElement && cb.checked) {
                values.push(cb.value);
            }
        });

        collector.value = values.join(',');
        collector.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

function initLoadMoreIntercept(archiveType) {
    const container = document.querySelector(SELECTORS.paginationContainer);
    if (!container) return;

    container.addEventListener('click', (e) => {
        const button = e.target.closest(SELECTORS.loadMoreButton);
        if (!button) return;
        e.preventDefault();
        handleLoadMore(button, archiveType);
    });
}

function handlePopstate() {
    const form = document.querySelector(SELECTORS.form);
    if (!form) return;

    const archiveType = form.dataset.archiveType;
    if (!archiveType) {
        window.location.reload();
        return;
    }

    const currentUrl = window.location.href;
    setLoadingState(true);

    // Popstate bij load-more: `?loaded=N` wordt naar REST endpoint
    // doorgegeven zodat pages 1..N in één response terugkomen (replace
    // mode, niet append — de hele posts-container wordt vervangen).
    // `fetchArchive` kopieert alle URL-params door naar REST, inclusief
    // `loaded`, dus geen aparte code nodig — REST handler doet de merge.
    fetchArchive(currentUrl, archiveType)
        .then((data) => {
            updateDOM(data);
            syncFormFromUrl(form, currentUrl);
            // Popstate = schone start: actief panel (drawer of modal)
            // sluiten, body-scroll-lock vrijgeven. Nieuwe URL-state is
            // leading — geen aanname dat de vorige panel-open-stand
            // nog relevant is.
            const cfg = getActivePanelConfig();
            if (cfg) _setPanelState(false, cfg);
            reinitAfterDomUpdate(archiveType);
        })
        .catch((error) => {
            console.error('Archive popstate AJAX failed:', error);
            window.location.reload();
        })
        .finally(() => setLoadingState(false));
}

/**
 * Public init: koppelt listeners aan het anker-form, de sort select en
 * `popstate`.
 *
 * @param {{ baseUrl?: string }} [options]
 */
export function initArchiveUrl(options = {}) {
    const form = document.querySelector(SELECTORS.form);
    if (!form) return;

    const baseUrl = options.baseUrl || window.location.pathname;
    const sortSelect = document.querySelector(SELECTORS.sortSelect);
    const defaults = {};
    if (sortSelect && sortSelect.dataset.defaultSort) {
        defaults.sort = sortSelect.dataset.defaultSort;
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        // Bij instant-mode: clear pending debounce zodat een expliciete
        // submit (Enter, noscript-button) niet dubbel fetcht.
        if (instantDebounceTimer !== null) {
            clearTimeout(instantDebounceTimer);
            instantDebounceTimer = null;
        }
        handleActionAjax(form, baseUrl, defaults, { closeDrawer: shouldCloseActivePanel() });
    });

    if (sortSelect) {
        sortSelect.addEventListener('change', () => {
            // Sort change op desktop responsive: drawer blijft open
            // (gebruiker kan direct resultaat zien én verder filteren).
            // Overlay/mobile: drawer sluit zodat content zichtbaar wordt.
            handleActionAjax(form, baseUrl, defaults, { closeDrawer: shouldCloseActivePanel() });
        });
    }

    // Instant-filter mode: hook change-events op alle
    // filter-controls via event-delegation op document, zodat de
    // listener AJAX innerHTML-updates van de filters-container
    // overleeft. Checkbox-sync listener is al eerder geregistreerd en
    // runt dus vóór deze — hidden-input waardes zijn up-to-date.
    const interaction = form.dataset.interaction || 'apply';
    if (interaction === 'instant') {
        document.addEventListener('change', (e) => {
            const target = e.target;
            if (!(target instanceof HTMLElement)) return;
            // Sort-select heeft zijn eigen change-listener hierboven — skip.
            if (target.matches(SELECTORS.sortSelect)) return;
            // Alleen controls die logisch deel zijn van archive-form.
            const formAttr = target.getAttribute('form');
            if (formAttr !== form.id && !target.closest(`#${form.id}`)) return;
            // Buttons zijn geen filter-changes.
            const tag = target.tagName;
            if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return;

            handleInstantFilter(form, baseUrl, defaults);
        });
    }

    if (form.dataset.archiveType) {
        initLinkIntercept(SELECTORS.paginationContainer, form.dataset.archiveType, {
            skipFilters: true,
        });
        initLinkIntercept(SELECTORS.filtersContainer, form.dataset.archiveType, {
            closeDrawer: true,
        });
        initLoadMoreIntercept(form.dataset.archiveType);
        initCheckboxSync();
        // Init drawer, modal, bar — alleen één bestaat in DOM per
        // pagina; de andere init-calls zijn no-ops (geen root-element
        // gevonden).
        initDrawer();
        initModal();
        initBar();

        // Active-filter tag removal. Event-delegation
        // op document zodat AJAX-updates van de active-filters container
        // de listener overleven.
        document.addEventListener('click', (e) => {
            if (!(e.target instanceof Element)) return;
            const removeBtn = e.target.closest(SELECTORS.tagRemove);
            if (!(removeBtn instanceof HTMLElement)) return;
            e.preventDefault();
            handleTagRemove(removeBtn, form, baseUrl, defaults);
        });

        // "Wis alles"-link in de active-filters container is een <a>
        // — laat hem via AJAX afgehandeld worden door de generieke
        // link-intercept in plaats van een page reload.
        initLinkIntercept(SELECTORS.activeFiltersContainer, form.dataset.archiveType, {
            skipFilters: false,
        });
        // Infinite scroll: observer wordt pas aangezet als de partial een
        // sentinel rendert (niet de initial load bij leeg resultaat of
        // bij numeric/load_more type). `initInfiniteScrollIntercept`
        // returnt stil wanneer geen container aanwezig is.
        initInfiniteScrollIntercept(form.dataset.archiveType);
    }

    window.addEventListener('popstate', handlePopstate);
}
